import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppConstants } from 'src/app/constants/app-constants';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SessionService } from 'src/app/services/session/session.service';
import { UserService } from 'src/app/services/user/user.service';

/**
 * ExtendSessionDialogComponent opens up a dialog to prompt the user on whether or
 * not they'd like to extend their session
 */
@Component({
    selector: 'app-extend-session-dialog',
    templateUrl: './extend-session-dialog.component.html',
    styleUrl: './extend-session-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ExtendSessionDialogComponent implements OnInit, OnDestroy {
  /** Dependency injections */
  public dialogRef: MatDialogRef<ExtendSessionDialogComponent> = inject(MatDialogRef<ExtendSessionDialogComponent>);
  private _authService: AuthService = inject(AuthService);
  private _router: Router = inject(Router);
  private _sessionService: SessionService = inject(SessionService);

  /** Session time countdown */
  public sessionTimeRemaining: string = "";

  /** Subscription to session time changes */
  private sessionSubscription: Subscription | null = null;

  /**
   * On init, subscribe to the session time remaining observable
   */
  ngOnInit(): void {
    // Subscribe to time changes
    this.sessionSubscription = this._sessionService.clock$.subscribe(
      (timestamp: string) => {
        this.sessionTimeRemaining = timestamp;

        // If the time is up, logout and close the dialog
        if (timestamp === "0:00") {
          this.logout();
        }
      }
    );
  }

  /**
   * On destroy, unsubscribe from the session time remaining observable
   */
  ngOnDestroy(): void {
    if (this.sessionSubscription) {
      this.sessionSubscription.unsubscribe();
      this.sessionSubscription = null;
    }
  }

  /**
   * Log the user out and navigate them to the login screen
   */
  public logout() {
    this._authService.logout();
    this.dialogRef.close();
    this._router.navigate([AppConstants.MENU_LOGIN]);
  }

  /**
   * Extend the user's session
   */
  public extendSession() {
    this._sessionService.hardRefresh();
    this.dialogRef.close();
  }
}
